import React, { useEffect, useState, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Document, Page, pdfjs } from "react-pdf";
import { ShieldCheck, ZoomIn, ZoomOut, Maximize2, Box, AlertCircle } from "lucide-react";

import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// PDF Worker Engine Pipeline
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PortfolioFAQViewer = ({ show, onHide, pdfUrl }) => {
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0); // Interactive scale zoom state
  const [pageWidth, setPageWidth] = useState(900);
  const containerRef = useRef(null);

  // Dynamic calculation for responsive canvas bounding widths
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const structuralWidth = containerRef.current.clientWidth;
        setPageWidth(structuralWidth > 960 ? 920 : structuralWidth - 32);
      }
    };

    if (show) {
      setTimeout(handleResize, 150); // allow modal animation thread space
      window.addEventListener("resize", handleResize);
    }
    return () => window.removeEventListener("resize", handleResize);
  }, [show, scale]);

  // High-Security Data Protection Stream Rules
  useEffect(() => {
    const preventActions = (e) => {
      // Disable Context Menu Right Click
      if (e.type === "contextmenu") {
        e.preventDefault();
      }
      
      // Defend cryptographic document endpoints against typical key capture methods
      if (e.ctrlKey || e.metaKey) {
        if (["s", "p", "c", "u", "i", "j"].includes(e.key.toLowerCase())) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    if (show) {
      window.addEventListener("contextmenu", preventActions);
      window.addEventListener("keydown", preventActions, true);
    }

    return () => {
      window.removeEventListener("contextmenu", preventActions);
      window.removeEventListener("keydown", preventActions, true);
    };
  }, [show]);

  // Clean up Blob URLs properly on component unmount
  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.15, 1.5));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.15, 0.6));
  const resetZoom = () => setScale(1.0);

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      centered
      className="enterprise-pdf-modal font-sans-interface"
    >
      {/* HIGH-END GOVERNANCE COMPLIANCE ACTION HEADER HEADERBAR */}
      <Modal.Header closeButton className="bg-slate-950 text-white border-slate-800 py-2.5 px-3">
        <Modal.Title className="d-flex align-items-center gap-2 m-0" style={{ fontSize: "0.85rem" }}>
          <Box size={14} className="text-success" />
          <span className="fw-bold tracking-tight text-white uppercase">Product Asset Architecture Hub</span>
          <span className="mx-1 text-slate-700">•</span>
          <span className="text-slate-400 font-normal small">Internal Verification FAQ Reader</span>
        </Modal.Title>
      </Modal.Header>

      {/* CORE ACTIVE UTILITIES PANEL CONTROLS TOOLBAR */}
      <div className="bg-slate-900 border-bottom border-slate-800 py-1.5 px-3 d-flex align-items-center justify-content-between text-white shadow-sm font-monospace">
        <div className="d-flex align-items-center gap-2 text-slate-400 extra-small security-flag">
          <ShieldCheck size={13} className="text-success animate-pulse" />
          <span className="tracking-wider uppercase">AES-256 Protected Viewing Isolation Layer</span>
        </div>

        {/* Dynamic Zoom Scale Interactive Action Nodes */}
        {pdfUrl && numPages && (
          <div className="d-flex align-items-center gap-1.5 toolbar-control-cluster">
            <button onClick={handleZoomOut} disabled={scale <= 0.6} className="btn-toolbar-tool" title="Zoom Out">
              <ZoomOut size={13} />
            </button>
            <span onClick={resetZoom} className="zoom-indicator-badge cursor-pointer" title="Reset Zoom">
              {Math.round(scale * 100)}%
            </span>
            <button onClick={handleZoomIn} disabled={scale >= 1.5} className="btn-toolbar-tool" title="Zoom In">
              <ZoomIn size={13} />
            </button>
            <span className="mx-1 text-slate-800">|</span>
            <div className="page-count-badge">
              DOC CAPACITY: <span className="text-white fw-bold">{numPages} {numPages === 1 ? 'PAGE' : 'PAGES'}</span>
            </div>
          </div>
        )}
      </div>

      {/* DOCUMENT PAGE VIEWPORT CANVAS FRAME */}
      <Modal.Body
        ref={containerRef}
        className="m-0 p-3"
        style={{
          overflowY: "auto",
          background: "#0f172a", 
          userSelect: "none",
          WebkitUserSelect: "none"
        }}
      >
        <div className="pdf-render-canvas-envelope mx-auto d-flex flex-column align-items-center w-100 position-relative">
          {!pdfUrl ? (
            <div className="text-center py-5 mt-5">
              <Spinner animation="border" variant="primary" size="sm" className="mb-2" />
              <div className="text-slate-500 font-monospace extra-small tracking-widest uppercase">Decryption Stream Mounting...</div>
            </div>
          ) : (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <div className="text-center py-5 mt-5">
                  <Spinner animation="border" variant="success" size="sm" className="mb-2" />
                  <div className="text-slate-500 font-monospace extra-small tracking-widest uppercase">Buffering Page Layout Vectors...</div>
                </div>
              }
              error={
                <div className="text-center py-5 mt-5 text-danger font-monospace extra-small d-flex align-items-center gap-2 justify-content-center bg-danger bg-opacity-10 rounded border border-danger border-opacity-20 px-4">
                  <AlertCircle size={14} />
                  <span>CRITICAL ERROR: PRIVILEGED DOCUMENT STREAM FETCH FAILED</span>
                </div>
              }
            >
              {numPages &&
                Array.from(new Array(numPages), (el, index) => (
                  <div
                    key={index}
                    className="pdf-page-container mb-3 position-relative shadow-lg border border-dark rounded-1 overflow-hidden"
                    style={{ transition: "transform 0.2s ease" }}
                  >
                    {/* CORE ADDITION: SECURE DIAGONAL WATERMARK CANVAS WRAPPER */}
                    <div className="secure-document-watermark-mask">
                      <div className="watermark-diagonal-text">Organitaz @ 2026</div>
                      <div className="watermark-diagonal-text">ORGAVITAZ | PRODUCT INFORMATION</div>
                      <div className="watermark-diagonal-text">CONFIDENTIAL • INTERNAL USE ONLY</div>
                    </div>

                    <Page
                      pageNumber={index + 1}
                      width={pageWidth * scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                    
                    {/* Footnote watermark badge code stamped to the page layout corner */}
                    <div className="page-watermark-overlay font-monospace">ORGAVITAZ COMPLIANCE VERIFIED</div>
                  </div>
                ))
              }
            </Document>
          )}
        </div>
      </Modal.Body>

      <style>{`
        .font-sans-interface {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        .extra-small { font-size: 0.58rem !important; }
        .gap-1.5 { gap: 0.38rem !important; }

        .bg-slate-900 { background-color: #0f172a !important; }
        .bg-slate-950 { background-color: #020617 !important; }
        .border-slate-800 { border-color: #1e293b !important; }
        .text-success { color: #4ade80 !important; }
        .text-slate-400 { color: #94a3b8 !important; }
        .text-slate-500 { color: #64748b !important; }
        .text-slate-700 { color: #334155 !important; }
        .text-slate-800 { color: #1e293b !important; }

        /* Toolbar Controls Styling */
        .btn-toolbar-tool {
          background: #1e293b;
          border: 1px solid #334155;
          color: #cbd5e1;
          padding: 0.2rem 0.35rem;
          border-radius: 4px;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
          transition: all 0.12s ease-in-out;
        }
        .btn-toolbar-tool:hover:not(:disabled) {
          background: #334155;
          color: #ffffff;
        }
        .btn-toolbar-tool:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        .zoom-indicator-badge {
          font-size: 0.55rem;
          background: #020617;
          border: 1px solid #1e293b;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          color: #4ade80;
          font-weight: 700;
        }
        .page-count-badge {
          font-size: 0.55rem;
          color: #64748b;
          background: #020617;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          border: 1px solid #1e293b;
        }

        .pdf-page-container {
          background-color: #ffffff;
        }

        /* =========================================================
           ADVANCED INTERACTIVE PDF WATERMARK OVERLAY CSS
        ========================================================= */
        .secure-document-watermark-mask {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          align-items: center;
          z-index: 40;                /* Sits perfectly above the rendering canvas layer */
          pointer-events: none;       /* Allows clicking through transparency layers */
          user-select: none;
          overflow: hidden;
          opacity: 0.12;              /* Muted print-safe light alpha contrast index */
        }
        
        .watermark-diagonal-text {
          font-family: "Segoe UI", Helvetica, Arial, sans-serif;
          font-size: 2.0rem;
          font-weight: 900;
          color: #0f172a;
          letter-spacing: 0.2rem;
          transform: rotate(-32deg);  /* Clean geometric diagonal tilt angle */
          white-space: nowrap;
          text-shadow: 1px 1px 0px rgba(255,255,255,0.5);
        }

        .page-watermark-overlay {
          position: absolute;
          bottom: 8px;
          right: 12px;
          font-size: 0.45rem;
          color: #cbd5e1;
          font-weight: bold;
          letter-spacing: 0.05rem;
          opacity: 0.25;
          pointer-events: none;
          z-index: 41;
        }

        .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }

        .enterprise-pdf-modal .btn-close {
          filter: invert(1) grayscale(1) brightness(1.5);
          opacity: 0.6;
          padding: 0.5rem !important;
          font-size: 0.6rem;
        }
        .enterprise-pdf-modal .btn-close:hover { opacity: 1; }

        @media print {
          body *, .modal *, .enterprise-pdf-modal * {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
          }
        }
      `}</style>
    </Modal>
  );
};

export default PortfolioFAQViewer;